import React, { useEffect, useState, useMemo } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { apiFetch } from "../../api/apiFetch";
import { formatNumber } from "../../utils/format";
import ErrorModal from "../../components/ErrorModal";

const GRANULARITEITEN = [
  { key: "week", label: "Week" },
  { key: "month", label: "Maand" },
  { key: "quarter", label: "Kwartaal" },
  { key: "year", label: "Jaar" },
];

// Single source of truth per persoon: kleur, totaal-veld en de categorie-rijen
// voor de detail-tab. De weergavevolgorde is configureerbaar (zie
// PERSON_ORDER_STORAGE_KEY) — dit object bepaalt enkel de eigenschappen per persoon.
const PERSON_DEFS = {
  katleen: {
    label: "Katleen",
    color: "#2563eb",
    colorClass: "text-blue-700",
    totalKey: "katleen_totaal",
    rows: [
      { key: "katleen_dermato", label: "Dermato" },
      { key: "katleen_esth_geen_btw", label: "Esth. (0%)" },
      { key: "katleen_esth_btw", label: "Esth. (BTW)" },
      { key: "katleen_prod_btw", label: "Producten" },
      { key: "katleen_overig", label: "Overig" },
      { key: "cash_katleen", label: "Cash", meta: true },
    ],
  },
  britt: {
    label: "Britt",
    color: "#9333ea",
    colorClass: "text-purple-700",
    totalKey: "britt_totaal",
    rows: [
      { key: "britt_geen_btw", label: "Dermato + Esth. (0%)" },
      { key: "britt_met_btw", label: "Esth. + Intake (BTW)" },
      { key: "britt_prod_btw", label: "Producten (BTW)" },
      { key: "britt_overig", label: "Overig" },
      { key: "cash_britt", label: "Cash", meta: true },
    ],
  },
  evelyne: {
    label: "Evelyne",
    color: "#0d9488",
    colorClass: "text-teal-700",
    totalKey: "evelyne_bruto",
    rows: [
      { key: "evelyne_bruto", label: "Bruto" },
      { key: "evelyne_huur", label: "Huur (26%)" },
      { key: "cash_evelyne", label: "Cash", meta: true },
    ],
  },
};

// De Totaal-tab staat buiten de configureerbare artsvolgorde en is altijd de
// eerste tab én de default: overzicht eerst, doorklikken voor detail.
const TOTAL_TAB_KEY = "totaal";
const TOTAL_TAB_COLOR = "#1f2937";

// Overige inkomstenfile-categorieën buiten de 3 artsen, voor de grafiek.
// Enkel het huur/commissie-deel van Evelyne en Saskia telt mee in het praktijk-
// totaal (zij innen zelf en storten een percentage door) — hun bruto-bedragen
// horen dus niet thuis in deze gestapelde grafiek (staan wel in de Totaal-tab
// ter info). Alleen categorieën met een werkelijke waarde (>0 over de gekozen
// periodes) worden getoond.
const EXTRA_INCOME_SERIES = [
  { key: "evelyne_huur", label: "Evelyne (Huur)", color: "#06b6d4" },
  { key: "isabelle_huur", label: "Isabelle (Huur)", color: "#f97316" },
  { key: "julie_huur", label: "Julie (Huur)", color: "#eab308" },
  { key: "saskia_huur", label: "Saskia (Huur)", color: "#16a34a" },
  { key: "divers_geen_btw", label: "Divers (0%)", color: "#94a3b8" },
  { key: "divers_met_btw", label: "Divers (BTW)", color: "#475569" },
  { key: "tele_consult", label: "Tele-consult", color: "#8b5cf6" },
  { key: "efact", label: "E-FACT", color: "#ec4899" },
];

// Kleuren voor vergelijkingsjaren — bewust neutraal/gedempt en losstaand van de
// categoriekleuren hierboven, zodat een vergelijkingsbalk nooit verward wordt
// met een echte categorie van het referentiejaar.
const COMPARE_YEAR_COLORS = ["#94a3b8", "#f59e0b", "#0ea5e9", "#a855f7"];

// Trendlijn (voorspelling resterend jaar) — bewust een eigen kleur en lijnstijl
// (gestreept), losstaand van alle categorie-/vergelijkingskleuren.
const TREND_COLOR = "#7c3aed";

// Hoeveel voorgaande jaren de trend-groeivoet meeneemt (gemiddelde van de
// jaar-op-jaar-verhoudingen over deze historiek) — degradeert vanzelf naar
// minder jaren als er nog geen 5+ jaar echte data beschikbaar is.
const TREND_HISTORY_YEARS = 5;

const DEFAULT_PERSON_ORDER = ["katleen", "britt", "evelyne"];
const PERSON_ORDER_STORAGE_KEY = "inkomstenfile_person_order";

function loadPersonOrder() {
  try {
    const raw = localStorage.getItem(PERSON_ORDER_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed) && parsed.length === 3 && parsed.every((k) => PERSON_DEFS[k])) {
      return parsed;
    }
  } catch {
    // ignore corrupt localStorage value, fall back to default
  }
  return DEFAULT_PERSON_ORDER;
}

function formatCurrency(value) {
  if (!value) return "—";
  return `€ ${formatNumber(value, 2)}`;
}

function sumField(periods, key) {
  return periods.reduce((acc, p) => acc + (p[key] || 0), 0);
}

// Heeft deze categorie ergens een echte waarde? Bewust NIET de som over alle
// periodes (zoals Tele-consult: de individuele maanden hebben substantiële
// bedragen, maar de jaarsom komt uit op ~0 doordat de Q4-omzetting naar E-FACT
// negatieve tele_consult-bedragen invoert die de positieve maanden opheffen).
function hasAnyValue(periods, key) {
  return periods.some((p) => Math.abs(p[key] || 0) > 0.005);
}

// Groei van het referentiejaar (huidig) t.o.v. het vergelijkingsjaar (bv.
// vorig jaar), als percentage met pijltje: groen/omhoog als het huidige jaar
// hoger uitkomt dan het vergelijkingsjaar (= vooruitgang), rood/omlaag als het
// lager uitkomt (= achteruitgang) — dus altijd vanuit "is dit jaar beter?".
// Zonder vergelijkingswaarde (0 of onbekend) is een percentage niet zinvol.
function formatDelta(refVal, compareVal) {
  const ref = refVal || 0;
  const cmp = compareVal || 0;
  if (!cmp) return { text: "—", colorClass: "text-gray-300" };
  const diffPct = ((ref - cmp) / Math.abs(cmp)) * 100;
  const rounded = Math.round(diffPct * 10) / 10;
  if (rounded === 0) return { text: "0,0%", colorClass: "text-gray-400" };
  const arrow = rounded > 0 ? "▲" : "▼";
  const colorClass = rounded > 0 ? "text-emerald-600" : "text-red-600";
  return { text: `${arrow} ${Math.abs(rounded).toFixed(1).replace(".", ",")}%`, colorClass };
}

function enrichPeriods(rawPeriods) {
  return rawPeriods.map((p) => ({
    ...p,
    totaal_excl_btw: Math.round((p.totaal_incl_btw - p.btw) * 100) / 100,
  }));
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Welk deel van een periode (start-eind) ligt nog vóór ons, gerekend in dagen?
// Gebruikt om de trend enkel nog op het nog-niet-voltooide stuk van een lopende
// periode toe te passen — het al verstreken (werkelijke) stuk telt als waarheid.
function remainingFraction(periodStart, periodEnd, today) {
  const start = new Date(periodStart);
  const end = new Date(periodEnd);
  const totalDays = Math.round((end - start) / MS_PER_DAY) + 1;
  if (totalDays <= 0) return 0;
  const elapsedDays = Math.min(totalDays, Math.max(0, Math.round((today - start) / MS_PER_DAY) + 1));
  return 1 - elapsedDays / totalDays;
}

// Eigen label voor de gemiddelde-lijn: tekent een lichte achtergrond achter de
// tekst zodat die leesbaar blijft ook als er een balkje achter geschoven zit.
function AverageReferenceLabel({ viewBox, value }) {
  if (!viewBox) return null;
  const text = `Gemiddelde: ${formatCurrency(value)}`;
  const charWidth = 6.2;
  const paddingX = 6;
  const rectWidth = text.length * charWidth + paddingX * 2;
  const rectHeight = 16;
  const x = viewBox.x + viewBox.width - rectWidth - 4;
  const y = viewBox.y - rectHeight - 2;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={rectWidth}
        height={rectHeight}
        rx={3}
        fill="#fef2f2"
        fillOpacity={0.92}
        stroke="#fecaca"
        strokeWidth={1}
      />
      <text
        x={x + rectWidth / 2}
        y={y + rectHeight / 2 + 4}
        textAnchor="middle"
        fontSize={11}
        fontWeight={600}
        fill="#dc2626"
      >
        {text}
      </text>
    </g>
  );
}

// Table: rows = categories, one column per visible period. Zelfde look and feel
// als de rest van de app (lichte bg-gray-50-header, geen zebra, divide-y,
// hover:bg-gray-50) i.p.v. het eerdere donkere/amber eigen thema. Rij-stijlen:
// - (geen vlag): gewone categorie-rij
// - meta: afgeleide info zoals Cash (lichte amber-highlight, zoals elders in de
//   app kleur gebruikt wordt om één specifieke rij te laten opvallen)
// - emphasis: totaalrijen (vet, lichte grijze achtergrond)
// - info: bruto-bedragen die niet in het totaal zitten (grijs, cursief, "niet in totaal")
//
// compareYears/comparePeriodsByYear: optionele vergelijkingsjaren. Onder elke
// data-rij komt dan, per vergelijkingsjaar, een extra sub-rij met de absolute
// waarde van dat jaar (zelfde periode-index) en het verschilpercentage t.o.v.
// de referentierij (groen/pijl-omhoog bij stijging, rood/pijl-omlaag bij daling).
//
// trendRowPeriods: optioneel, één array (uitgelijnd op periode-index) met per
// periode een object van getrende veldwaarden — enkel gevuld voor periodes die
// nog niet volledig afgelopen zijn. De trendwaarde wordt, als showTrend
// aanstaat, mee op dezelfde lijn als het werkelijke cijfer getoond (eigen
// kleur + cursief), niet als aparte rij. Is er nog geen werkelijk cijfer voor
// die periode, dan wordt enkel de trendwaarde getoond (geen streepje ernaast).
// De Totaal-kolom toont er het verwachte jaartotaal bij (werkelijk voor
// afgelopen periodes + projectie voor de rest). De vergelijkingsjaren-rijen
// tonen enkel de werkelijke vergelijking, geen trend.
function PeriodBreakdownTable({
  periods, rows, note,
  compareYears = [], comparePeriodsByYear = {},
  showTrend = false, trendRowPeriods = null,
}) {
  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-wider text-xs sticky left-0 bg-gray-50 whitespace-nowrap">
                Categorie
              </th>
              {periods.map((p) => (
                <th key={p.label} className="px-4 py-3 text-right font-bold text-gray-500 uppercase tracking-wider text-xs whitespace-nowrap">
                  {p.label}
                </th>
              ))}
              <th className="px-4 py-3 text-right font-bold text-gray-700 uppercase tracking-wider text-xs whitespace-nowrap border-l-2 border-gray-300 bg-gray-100">
                Totaal
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((row) => {
              const rowBg = row.meta
                ? "bg-amber-50"
                : row.emphasis
                  ? "bg-gray-100 border-t-2 border-gray-300"
                  : "bg-white hover:bg-gray-50";
              const labelCell = row.meta
                ? "font-semibold text-amber-800"
                : row.emphasis
                  ? "font-bold text-gray-900"
                  : row.info
                    ? "font-medium italic text-gray-400"
                    : "font-medium text-gray-700";
              const dataCell = row.meta
                ? "text-amber-700"
                : row.emphasis
                  ? "font-bold text-gray-900"
                  : row.info
                    ? "italic text-gray-400"
                    : "text-gray-600";
              const totalColBg = row.meta
                ? "bg-amber-100"
                : row.emphasis
                  ? "bg-gray-200"
                  : "bg-gray-50";
              // Border op de tr zelf toont in de meeste browsers enkel bij de sticky
              // kolom (tabel-rijen renderen geen eigen rand in het "separate borders"
              // model) — daarom expliciet op elke cel gezet, niet enkel op de <tr>.
              const emphasisBorder = row.emphasis ? "border-t-2 border-gray-300" : "";

              // Heeft deze rij ergens een trendwaarde (verschillend van gewoon niets)?
              // Bepaalt of de trend-projectie in de Totaal-kolom getoond wordt.
              const rowHasTrend = showTrend && !!trendRowPeriods &&
                trendRowPeriods.some((tp) => tp && tp[row.key] != null);
              const trendTotal = rowHasTrend
                ? periods.reduce((sum, p, pi) => {
                    const tp = trendRowPeriods[pi];
                    const val = tp && tp[row.key] != null ? tp[row.key] : (p[row.key] || 0);
                    return sum + val;
                  }, 0)
                : null;

              return (
                <React.Fragment key={row.key}>
                  <tr className={rowBg}>
                    <td className={`px-4 py-2.5 sticky left-0 whitespace-nowrap ${rowBg} ${labelCell} ${emphasisBorder}`}>
                      {row.label}
                      {row.info && (
                        <span className="ml-1.5 text-xs font-normal not-italic text-gray-400">
                          (niet in totaal)
                        </span>
                      )}
                    </td>
                    {periods.map((p, pi) => {
                      const tp = showTrend && trendRowPeriods ? trendRowPeriods[pi] : null;
                      const trendVal = tp ? tp[row.key] : null;
                      const hasActual = !!p[row.key];
                      return (
                        <td key={p.label} className={`px-4 py-2.5 text-right tabular-nums whitespace-nowrap ${dataCell} ${emphasisBorder}`}>
                          {hasActual || trendVal == null ? (
                            <div>{formatCurrency(p[row.key])}</div>
                          ) : null}
                          {trendVal != null && (
                            <div className="italic text-xs" style={{ color: TREND_COLOR }}>
                              {formatCurrency(trendVal)}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className={`px-4 py-2.5 text-right tabular-nums font-bold whitespace-nowrap border-l-2 border-gray-300 ${totalColBg} ${dataCell} ${emphasisBorder}`}>
                      <div>{formatCurrency(sumField(periods, row.key))}</div>
                      {rowHasTrend && (
                        <div className="italic text-xs font-normal" style={{ color: TREND_COLOR }}>
                          {formatCurrency(trendTotal)}
                        </div>
                      )}
                    </td>
                  </tr>
                  {compareYears.map((y, yi) => {
                    const cPeriods = comparePeriodsByYear[y] || [];
                    const color = COMPARE_YEAR_COLORS[yi % COMPARE_YEAR_COLORS.length];
                    const compareRowBg = row.meta ? "bg-amber-50/50" : row.emphasis ? "bg-gray-50" : "bg-gray-50/40";
                    return (
                      <tr key={`${row.key}-cmp-${y}`} className={compareRowBg}>
                        <td className={`pl-7 pr-4 py-1.5 sticky left-0 whitespace-nowrap text-xs italic ${compareRowBg}`} style={{ color }}>
                          vs {y}
                        </td>
                        {periods.map((p, pi) => {
                          const cVal = cPeriods[pi]?.[row.key];
                          // Voor een periode die nog niet begonnen/afgerond is, is het
                          // werkelijke cijfer altijd 0 — een vergelijking daarmee geeft
                          // dan zinloos steeds -100%. Staat de trend aan en is er een
                          // trendwaarde voor deze periode, gebruik die als referentie
                          // i.p.v. het (nog altijd 0) werkelijke cijfer.
                          const tp = showTrend && trendRowPeriods ? trendRowPeriods[pi] : null;
                          const trendVal = tp ? tp[row.key] : null;
                          const delta = formatDelta(trendVal != null ? trendVal : p[row.key], cVal);
                          return (
                            <td key={p.label} className="px-4 py-1.5 text-right whitespace-nowrap">
                              <div className="text-xs tabular-nums text-gray-500">{formatCurrency(cVal)}</div>
                              <div className={`text-[10px] font-semibold tabular-nums ${delta.colorClass}`}>{delta.text}</div>
                            </td>
                          );
                        })}
                        {(() => {
                          const cTotal = sumField(cPeriods, row.key);
                          const refTotal = sumField(periods, row.key);
                          const delta = formatDelta(rowHasTrend ? trendTotal : refTotal, cTotal);
                          return (
                            <td className="px-4 py-1.5 text-right whitespace-nowrap border-l-2 border-gray-300 bg-gray-50/60">
                              <div className="text-xs font-semibold tabular-nums text-gray-600">{formatCurrency(cTotal)}</div>
                              <div className={`text-[10px] font-semibold tabular-nums ${delta.colorClass}`}>{delta.text}</div>
                            </td>
                          );
                        })()}
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400 px-4 py-2 border-t border-gray-100 bg-gray-50">
        Alle bedragen incl. BTW, tenzij anders vermeld.{note ? ` ${note}` : ""}
      </p>
    </div>
  );
}

// Eén gecombineerd instellingenpaneel voor alles wat de grafiek/tabs beïnvloedt:
// volgorde van de artsen, vergelijkingsjaren en de trendlijn-toggle. Vervangt de
// vroegere aparte "+ Vergelijk jaar"- en "⚙ Volgorde"-knoppen.
function ChartSettingsConfig({
  order, onOrderChange,
  years, jaar, compareYears, onToggleCompareYear,
  showTrend, onToggleTrend,
  granulariteit,
  onClose,
}) {
  const move = (index, direction) => {
    const next = [...order];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onOrderChange(next);
  };

  return (
    <div className="absolute right-0 top-full mt-1 z-10 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-64">
      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Volgorde artsen</div>
      {order.map((key, i) => (
        <div key={key} className="flex items-center justify-between py-1">
          <span className="text-sm text-gray-700">{i + 1}. {PERSON_DEFS[key].label}</span>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => move(i, -1)}
              disabled={i === 0}
              className="px-1.5 text-gray-500 hover:text-gray-800 disabled:opacity-25"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={() => move(i, 1)}
              disabled={i === order.length - 1}
              className="px-1.5 text-gray-500 hover:text-gray-800 disabled:opacity-25"
            >
              ↓
            </button>
          </div>
        </div>
      ))}

      {granulariteit !== "year" && (
        <>
          <div className="text-xs font-semibold text-gray-500 uppercase mt-3 mb-2 border-t border-gray-100 pt-3">
            Vergelijk met
          </div>
          {years.filter((y) => y !== jaar).map((y) => (
            <label key={y} className="flex items-center gap-2 py-1 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={compareYears.includes(y)}
                onChange={() => onToggleCompareYear(y)}
                className="rounded border-gray-300"
              />
              {y}
            </label>
          ))}
        </>
      )}

      <div className="text-xs font-semibold text-gray-500 uppercase mt-3 mb-2 border-t border-gray-100 pt-3">
        Trend
      </div>
      <label className="flex items-center gap-2 py-1 text-sm text-gray-700 cursor-pointer">
        <input
          type="checkbox"
          checked={showTrend}
          onChange={() => onToggleTrend(!showTrend)}
          className="rounded border-gray-300"
        />
        {granulariteit === "year" ? "Toon trend lopend jaar" : "Toon trend resterend jaar"}
      </label>

      <button
        type="button"
        onClick={onClose}
        className="mt-3 pt-2 border-t border-gray-100 w-full text-xs text-blue-600 hover:text-blue-800 text-center"
      >
        Sluiten
      </button>
    </div>
  );
}

export default function ResultsInkomstenfile({ user }) {
  const currentYear = new Date().getFullYear();
  const [granulariteit, setGranulariteit] = useState("quarter");
  const [jaar, setJaar] = useState(currentYear);
  const [kwartaal, setKwartaal] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });
  const [hiddenSeries, setHiddenSeries] = useState(new Set());
  const [personOrder, setPersonOrder] = useState(loadPersonOrder);
  const [selectedTab, setSelectedTab] = useState(TOTAL_TAB_KEY);
  const [compareYears, setCompareYears] = useState([]);
  const [compareData, setCompareData] = useState({});
  const [showTrend, setShowTrend] = useState(true);
  const [showSettingsConfig, setShowSettingsConfig] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ granulariteit, jaar: String(jaar) });
    if (granulariteit === "week") params.set("kwartaal", String(kwartaal));
    apiFetch(`/results/summaries/inkomstenfile/timeseries?${params.toString()}`)
      .then(setData)
      .catch((err) =>
        setErrorModal({ show: true, message: err.message || "Fout bij ophalen van data." })
      )
      .finally(() => setLoading(false));
  }, [granulariteit, jaar, kwartaal]);

  // Vergelijkingsjaren-/trenddata: één timeseries-fetch per benodigd jaar. Voor
  // week/maand/kwartaal op dezelfde granulariteit/kwartaal zodat de periodes
  // 1-op-1 uitlijnen met het referentiejaar. Jaargranulariteit heeft al jaren
  // als periodes, dus expliciete jaarvergelijking is daar niet zinvol — maar
  // de trend voor het lopende (nog niet afgelopen) jaar wordt daar wél
  // ondersteund: dan wordt op MAANDNIVEAU opgehaald (ook voor het lopende jaar
  // zelf, nodig om te weten hoeveel maanden al verstreken zijn), losstaand van
  // de jaargranulariteit die op het scherm getoond wordt.
  useEffect(() => {
    const fetchGranulariteit = granulariteit === "year" ? "month" : granulariteit;
    const yearsToFetch = new Set(compareYears);
    if (showTrend) {
      if (granulariteit === "year") {
        yearsToFetch.add(jaar);
      }
      for (let n = 1; n <= TREND_HISTORY_YEARS; n++) yearsToFetch.add(jaar - n);
    }
    const list = [...yearsToFetch];

    if (list.length === 0) {
      setCompareData({});
      return undefined;
    }
    let cancelled = false;

    // Na elkaar ophalen (niet Promise.all) — de trend kan tot 5 extra jaren in
    // één keer opvragen, en die allemaal tegelijk afvuren overbelast de lokale
    // dev-backend (één worker), wat zich als een CORS-fout in de browser
    // voordoet zodra een request daardoor faalt/timet i.p.v. een net antwoord.
    (async () => {
      const next = {};
      for (const y of list) {
        if (cancelled) return;
        const params = new URLSearchParams({ granulariteit: fetchGranulariteit, jaar: String(y) });
        if (fetchGranulariteit === "week") params.set("kwartaal", String(kwartaal));
        try {
          const res = await apiFetch(`/results/summaries/inkomstenfile/timeseries?${params.toString()}`);
          next[y] = res.periods;
        } catch {
          // Jaar zonder bruikbaar resultaat (bv. geen data) — gewoon overslaan.
        }
      }
      if (!cancelled) setCompareData(next);
    })();

    return () => {
      cancelled = true;
    };
  }, [granulariteit, kwartaal, compareYears, showTrend, jaar]);

  // Verrijk de periods met een client-side afgeleide excl.-BTW-waarde zodat de
  // Totaal-tab er transparant mee kan werken (bestaat niet in de API-response).
  const periods = useMemo(() => (data ? enrichPeriods(data.periods) : []), [data]);

  // Vergelijkingsjaren die ook effectief zinvol zijn: het referentiejaar zelf
  // uitsluiten (bv. als jaar via de dropdown gewijzigd is naar een jaar dat nog
  // als vergelijking aangevinkt stond) — anders krijg je een zinloze "vs
  // zichzelf"-rij/balk met 0,0% verschil overal. Bij jaargranulariteit is
  // expliciete jaarvergelijking sowieso niet zinvol (de periodes zijn al
  // jaren) — en sinds de trend daar op maandniveau ophaalt, zou een blijvend
  // aangevinkt vergelijkingsjaar anders per ongeluk maandata in de jaarweergave
  // binnensluipen.
  const activeCompareYears = useMemo(
    () => (granulariteit === "year" ? [] : compareYears.filter((y) => y !== jaar)),
    [compareYears, jaar, granulariteit]
  );

  // Idem voor elk vergelijkingsjaar, uitgelijnd op periode-index met `periods`.
  const comparePeriodsByYear = useMemo(() => {
    const result = {};
    for (const y of activeCompareYears) {
      if (compareData[y]) result[y] = enrichPeriods(compareData[y]);
    }
    return result;
  }, [activeCompareYears, compareData]);

  // Welke overige categorieën (buiten de 3 artsen) hebben effectief een waarde?
  // Twee zaken bewust gecorrigeerd t.o.v. een simpele jaarsom:
  // 1. Niet enkel in het referentiejaar kijken: een categorie die structureel 0
  //    is in het referentiejaar maar wél waarde heeft in een vergelijkings-/
  //    trendjaar (bv. Tele-consult: bestaat enkel in 2025) moet ook dan
  //    meetellen — anders valt die stilzwijgend weg uit de grafiekbalk/trend
  //    van dat andere jaar, terwijl de tabel (die rechtstreeks totaal_incl_btw
  //    toont) het wel meerekent.
  // 2. Per periode kijken (hasAnyValue), niet de som over alle periodes: Tele-
  //    consult heeft in 2025 zowel positieve maanden (Q1-Q3) als negatieve
  //    (de Q4-omzetting naar E-FACT), die elkaar in de jaarsom opheffen tot
  //    ~0 — een sumField-check zou de categorie dan ten onrechte uitsluiten.
  const visibleExtraSeries = useMemo(() => {
    const otherPeriodSets = Object.values(compareData);
    return EXTRA_INCOME_SERIES.filter(
      (s) => hasAnyValue(periods, s.key) || otherPeriodSets.some((ps) => hasAnyValue(ps, s.key))
    );
  }, [periods, compareData]);

  // DataKeys van de effectief gestapelde balkjes (bruto-bedragen tellen niet mee,
  // die worden nooit als bar getoond — zie hieronder).
  const stackedSeriesKeys = useMemo(() => [
    ...personOrder
      .filter((key) => !PERSON_DEFS[key].totalKey.endsWith("_bruto"))
      .map((key) => PERSON_DEFS[key].label),
    ...visibleExtraSeries.map((s) => s.label),
  ], [personOrder, visibleExtraSeries]);

  // Terugkoppeling van chart-label naar het onderliggende veld, zodat de
  // vergelijkingsjaren-balk exact dezelfde (via de legende gefilterde) selectie
  // kan optellen uit de data van het vergelijkingsjaar.
  const seriesFieldByLabel = useMemo(() => {
    const map = {};
    for (const key of personOrder) {
      const def = PERSON_DEFS[key];
      if (!def.totalKey.endsWith("_bruto")) map[def.label] = def.totalKey;
    }
    for (const s of EXTRA_INCOME_SERIES) {
      map[s.label] = s.key;
    }
    return map;
  }, [personOrder]);

  const chartData = useMemo(() => {
    // Vergelijkingsjaren tellen enkel de reeksen op die op dit moment ook echt
    // zichtbaar zijn (niet via de legende verborgen) — zo vergelijkt "enkel
    // Britt zichtbaar" met Britt van het vergelijkingsjaar, niet met het
    // volledige praktijktotaal van dat jaar.
    const visibleLabels = stackedSeriesKeys.filter((label) => !hiddenSeries.has(label));
    return periods.map((p, i) => {
      const row = { label: p.label, period_start: p.period_start };
      for (const key of Object.keys(PERSON_DEFS)) {
        row[PERSON_DEFS[key].label] = p[PERSON_DEFS[key].totalKey];
      }
      for (const s of EXTRA_INCOME_SERIES) {
        row[s.label] = p[s.key];
      }
      for (const y of activeCompareYears) {
        const cp = comparePeriodsByYear[y]?.[i];
        row[`compare_${y}`] = cp
          ? visibleLabels.reduce((sum, label) => sum + (cp[seriesFieldByLabel[label]] || 0), 0)
          : null;
      }
      return row;
    });
  }, [periods, activeCompareYears, comparePeriodsByYear, stackedSeriesKeys, seriesFieldByLabel, hiddenSeries]);

  // Historische data (tot TREND_HISTORY_YEARS jaar terug), los van de expliciete
  // vergelijkingsjaren-selectie — dient als basis + groeivoet voor de trend. Bij
  // jaargranulariteit wordt dit op MAANDNIVEAU opgehaald (zie fetch-effect
  // hierboven) en ook het lopende jaar zelf meegenomen (nodig om te weten
  // hoeveel maanden daarvan al verstreken zijn).
  const trendHistoryPeriodsByYear = useMemo(() => {
    const result = {};
    if (!showTrend) return result;
    const startN = granulariteit === "year" ? 0 : 1;
    for (let n = startN; n <= TREND_HISTORY_YEARS; n++) {
      const y = jaar - n;
      if (compareData[y]) result[y] = enrichPeriods(compareData[y]);
    }
    return result;
  }, [showTrend, granulariteit, jaar, compareData]);

  // Trend voor de rest van het lopende jaar, inclusief de lopende (nog niet
  // afgelopen) periode zelf: voor elke periode die nog niet volledig voorbij is
  // (period_end >= vandaag — dus ook bv. de huidige, gedeeltelijk-verstreken
  // maand/kwartaal), wordt de waarde van dezelfde periode vorig jaar (jaar-1)
  // genomen als basis, geschaald met een groeivoet gemiddeld over meerdere
  // jaar-op-jaar-verhoudingen (lopend jaar t.o.v. jaar-1 voor de VOLLEDIG
  // afgelopen periodes dit jaar, en jaar-1 t.o.v. jaar-2, jaar-2 t.o.v. jaar-3,
  // ... voor zover die jaren echte data bevatten) — robuuster dan op één enkel
  // jaar te steunen, en degradeert vanzelf naar minder jaren zolang er nog geen
  // 5+ jaar data in het systeem zit. Een lopende periode telt bewust NIET mee
  // in de groeivoet zelf (enkel/gedeeltelijke data zou die kunstmatig
  // vertekenen) — enkel volledig afgelopen periodes worden daarvoor gebruikt.
  // Respecteert dezelfde via-de-legende-verborgen selectie als de
  // vergelijkingsjaren-balken, zodat "enkel Britt zichtbaar" ook enkel Britt's
  // trend toont.
  const trendGrowthRate = useMemo(() => {
    if (!showTrend) return null;
    const priorYearPeriods = trendHistoryPeriodsByYear[jaar - 1];
    if (!priorYearPeriods) return null;

    const today = new Date();
    const visibleLabels = stackedSeriesKeys.filter((label) => !hiddenSeries.has(label));
    const sumVisible = (p) =>
      visibleLabels.reduce((sum, label) => sum + ((p && p[seriesFieldByLabel[label]]) || 0), 0);

    const ratios = [];

    // Meest recente paar: lopend jaar (enkel VOLLEDIG afgelopen periodes) vs jaar-1.
    // Bij jaargranulariteit gebeurt dit op maandniveau (trendHistoryPeriodsByYear[jaar]),
    // anders rechtstreeks op de getoonde `periods`.
    const thisYearElapsedBasis = granulariteit === "year" ? trendHistoryPeriodsByYear[jaar] : periods;
    if (thisYearElapsedBasis) {
      let elapsedThis = 0;
      let elapsedPrior = 0;
      thisYearElapsedBasis.forEach((p, i) => {
        if (p.period_end && new Date(p.period_end) < today) {
          const priorVal = sumVisible(priorYearPeriods[i]);
          if (priorVal > 0) {
            elapsedThis += sumVisible(p);
            elapsedPrior += priorVal;
          }
        }
      });
      if (elapsedPrior > 0) ratios.push(elapsedThis / elapsedPrior);
    }

    // Oudere paren: volledige jaren tegenover elkaar (jaar-1 vs jaar-2, enz.).
    for (let n = 1; n < TREND_HISTORY_YEARS; n++) {
      const newer = trendHistoryPeriodsByYear[jaar - n];
      const older = trendHistoryPeriodsByYear[jaar - n - 1];
      if (!newer || !older) continue;
      const newerSum = newer.reduce((sum, p) => sum + sumVisible(p), 0);
      const olderSum = older.reduce((sum, p) => sum + sumVisible(p), 0);
      if (olderSum > 0) ratios.push(newerSum / olderSum);
    }

    if (!ratios.length) return null;
    return ratios.reduce((a, b) => a + b, 0) / ratios.length;
  }, [showTrend, granulariteit, periods, trendHistoryPeriodsByYear, jaar, stackedSeriesKeys, seriesFieldByLabel, hiddenSeries]);

  const trendBasisPeriods = trendHistoryPeriodsByYear[jaar - 1] || null;

  // Per-periode, per-veld trend. Voor een lopende (nog niet afgelopen) periode
  // wordt het al verstreken, werkelijke stuk als waarheid genomen — de
  // projectie wordt enkel nog toegepast op het nog-niet-voltooide restant van
  // die periode (verhouding in dagen, via remainingFraction), geschaald met
  // dezelfde jaar-op-jaar groeivoet t.o.v. dezelfde periode vorig jaar. Voor
  // een periode die nog helemaal niet begonnen is, is er niets werkelijks en
  // is de "resterende fractie" gewoon 1 — dus een volledige projectie, zoals
  // voorheen. Bedient zowel de grafiek (via stackedSeriesKeys-som) als de
  // tabel (elke categorie-rij afzonderlijk).
  const trendRowPeriods = useMemo(() => {
    if (trendGrowthRate == null || !trendBasisPeriods) return null;
    const today = new Date();

    if (granulariteit === "year") {
      // trendBasisPeriods = de 12 maanden van jaar-1 — som ze tot één
      // jaartotaal als "vorig jaar, dezelfde periode" voor het lopende jaar.
      const priorYearTotal = {};
      for (const m of trendBasisPeriods) {
        for (const key of Object.keys(m)) {
          if (typeof m[key] === "number") {
            priorYearTotal[key] = (priorYearTotal[key] || 0) + m[key];
          }
        }
      }
      const yearPeriod = periods[periods.length - 1];
      const remFrac = yearPeriod?.period_start && yearPeriod?.period_end
        ? remainingFraction(yearPeriod.period_start, yearPeriod.period_end, today)
        : 1;
      const projected = {};
      for (const key of Object.keys(priorYearTotal)) {
        const actualSoFar = typeof yearPeriod?.[key] === "number" ? yearPeriod[key] : 0;
        projected[key] = Math.round((actualSoFar + priorYearTotal[key] * remFrac * trendGrowthRate) * 100) / 100;
      }
      return periods.map((p, i) => (i === periods.length - 1 ? projected : null));
    }

    return periods.map((p, i) => {
      const notYetConcluded = !p.period_end || new Date(p.period_end) >= today;
      if (!notYetConcluded) return null;
      const basis = trendBasisPeriods[i];
      if (!basis) return null;
      const remFrac = p.period_start && p.period_end
        ? remainingFraction(p.period_start, p.period_end, today)
        : 1;
      const row = {};
      for (const key of Object.keys(basis)) {
        if (typeof basis[key] === "number") {
          const actualSoFar = typeof p[key] === "number" ? p[key] : 0;
          row[key] = Math.round((actualSoFar + basis[key] * remFrac * trendGrowthRate) * 100) / 100;
        }
      }
      return row;
    });
  }, [trendGrowthRate, trendBasisPeriods, periods, granulariteit]);

  const trendSeries = useMemo(() => {
    if (!trendRowPeriods) return null;
    const visibleLabels = stackedSeriesKeys.filter((label) => !hiddenSeries.has(label));
    return trendRowPeriods.map((tp) =>
      tp ? visibleLabels.reduce((sum, label) => sum + (tp[seriesFieldByLabel[label]] || 0), 0) : null
    );
  }, [trendRowPeriods, stackedSeriesKeys, seriesFieldByLabel, hiddenSeries]);

  const chartDataWithTrend = useMemo(() => {
    if (!trendSeries) return chartData;
    return chartData.map((row, i) => ({ ...row, trend: trendSeries[i] }));
  }, [chartData, trendSeries]);

  // Gemiddelde van de getoonde balkjes — houdt rekening met via de legende
  // verborgen reeksen, zodat de lijn altijd overeenkomt met wat zichtbaar is.
  // Periodes zonder werkelijke data tellen niet mee, in beide richtingen: nog
  // niet-gestarte periodes (toekomst, bv. resterende maanden van het lopende
  // jaar) én periodes van vóór de data begint (bv. jaren 2022-2024 in de
  // jaargranulariteit, waar de praktijk nog niet in de app zat) — anders
  // trekken die lege periodes het gemiddelde kunstmatig omlaag.
  const chartAverage = useMemo(() => {
    const today = new Date();
    const rowTotal = (row) =>
      stackedSeriesKeys.reduce(
        (sum, key) => sum + (hiddenSeries.has(key) ? 0 : (row[key] || 0)),
        0
      );
    const totals = chartData
      .filter((row) => !row.period_start || new Date(row.period_start) <= today)
      .map(rowTotal)
      .filter((total) => total > 0);
    if (!totals.length) return 0;
    return totals.reduce((a, b) => a + b, 0) / totals.length;
  }, [chartData, stackedSeriesKeys, hiddenSeries]);

  // Rijen van de Totaal-tab: per arts (in geconfigureerde volgorde), gevolgd door
  // de overige inkomstenfile-posten (Isabelle/Julie/Saskia/Divers) en de totalen.
  const totalTabRows = useMemo(() => [
    ...personOrder.map((key) => {
      const def = PERSON_DEFS[key];
      const isBruto = def.totalKey.endsWith("_bruto");
      const label = isBruto ? `${def.label} (Bruto)` : def.label;
      return { key: def.totalKey, label, info: isBruto };
    }),
    { key: "evelyne_huur", label: "Evelyne (Huur)" },
    { key: "isabelle_huur", label: "Isabelle (Huur)" },
    { key: "julie_huur", label: "Julie (Huur)" },
    { key: "saskia_bruto", label: "Saskia (Bruto)", info: true },
    { key: "saskia_huur", label: "Saskia (Huur)" },
    { key: "divers_geen_btw", label: "Divers (0%)" },
    { key: "divers_met_btw", label: "Divers (BTW)" },
    { key: "tele_consult", label: "Tele-consult (2025)" },
    { key: "efact", label: "E-FACT (Q4 2025)" },
    { key: "totaal_incl_btw", label: "Totaal (incl. BTW)", emphasis: true },
    { key: "btw", label: "BTW" },
    { key: "totaal_excl_btw", label: "Totaal (excl. BTW)", emphasis: true },
    { key: "cash_totaal", label: "Cash", meta: true },
  ], [personOrder]);

  const years = [];
  for (let y = currentYear; y >= currentYear - 5; y--) years.push(y);

  const toggleSeries = (dataKey) => {
    setHiddenSeries((cur) => {
      const next = new Set(cur);
      if (next.has(dataKey)) next.delete(dataKey);
      else next.add(dataKey);
      return next;
    });
  };

  const updatePersonOrder = (next) => {
    setPersonOrder(next);
    localStorage.setItem(PERSON_ORDER_STORAGE_KEY, JSON.stringify(next));
  };

  const toggleCompareYear = (y) => {
    setCompareYears((cur) =>
      cur.includes(y) ? cur.filter((cy) => cy !== y) : [...cur, y].sort((a, b) => a - b)
    );
  };

  const tabDefs = [
    { key: TOTAL_TAB_KEY, label: "Totaal", color: TOTAL_TAB_COLOR, colorClass: "text-gray-900", totalKey: "totaal_incl_btw", rows: totalTabRows },
    ...personOrder.map((key) => ({ key, ...PERSON_DEFS[key] })),
  ];
  const activeTab = tabDefs.find((t) => t.key === selectedTab) || tabDefs[0];

  return (
    <div className="p-4 sm:p-6 max-w-full">
      {errorModal.show && (
        <ErrorModal
          message={errorModal.message}
          onClose={() => setErrorModal({ show: false, message: "" })}
        />
      )}

      <div className="flex flex-wrap items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Inkomstenfile</h1>

        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            {GRANULARITEITEN.map((g) => (
              <button
                key={g.key}
                onClick={() => setGranulariteit(g.key)}
                className={`px-3 py-1.5 text-sm font-medium transition ${
                  granulariteit === g.key
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>

          <select
            value={jaar}
            onChange={(e) => setJaar(Number(e.target.value))}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          {granulariteit === "week" && (
            <select
              value={kwartaal}
              onChange={(e) => setKwartaal(Number(e.target.value))}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={1}>Q1 (jan–mrt)</option>
              <option value={2}>Q2 (apr–jun)</option>
              <option value={3}>Q3 (jul–sep)</option>
              <option value={4}>Q4 (okt–dec)</option>
            </select>
          )}

          <div className="relative">
            <button
              type="button"
              onClick={() => setShowSettingsConfig((v) => !v)}
              title="Volgorde, vergelijkingsjaren en trend instellen"
              className="border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
            >
              ⚙ Instellingen
            </button>
            {showSettingsConfig && (
              <ChartSettingsConfig
                order={personOrder}
                onOrderChange={updatePersonOrder}
                years={years}
                jaar={jaar}
                compareYears={compareYears}
                onToggleCompareYear={toggleCompareYear}
                showTrend={showTrend}
                onToggleTrend={setShowTrend}
                granulariteit={granulariteit}
                onClose={() => setShowSettingsConfig(false)}
              />
            )}
          </div>
        </div>
      </div>

      {granulariteit !== "year" && compareYears.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap -mt-4 mb-6">
          <span className="text-xs text-gray-400">Vergelijking met:</span>
          {compareYears.map((y, i) => {
            const color = COMPARE_YEAR_COLORS[i % COMPARE_YEAR_COLORS.length];
            return (
              <span
                key={y}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                style={{ backgroundColor: `${color}22`, color }}
              >
                {y}
                <button
                  type="button"
                  onClick={() => toggleCompareYear(y)}
                  className="hover:opacity-70"
                  title={`Verwijder ${y} uit vergelijking`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      )}

      {!loading && data && (
        <>
          {periods.length === 0 && (
            <div className="text-gray-500 text-sm text-center py-12 bg-white rounded-lg border border-gray-200">
              Geen data voor deze selectie.
            </div>
          )}

          {periods.length > 0 && (
            <>
              {/* Trend chart */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
                <div className="text-sm font-semibold text-gray-700 mb-3">
                  Evolutie per {GRANULARITEITEN.find((g) => g.key === granulariteit)?.label.toLowerCase()} (incl. BTW)
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={chartDataWithTrend}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend
                      onClick={(e) => toggleSeries(e.dataKey)}
                      formatter={(value, entry) => (
                        <span
                          style={{
                            cursor: "pointer",
                            opacity: hiddenSeries.has(entry.dataKey) ? 0.4 : 1,
                            textDecoration: hiddenSeries.has(entry.dataKey) ? "line-through" : "none",
                          }}
                        >
                          {value}
                        </span>
                      )}
                    />
                    {personOrder
                      .filter((key) => !PERSON_DEFS[key].totalKey.endsWith("_bruto"))
                      .map((key) => {
                        const def = PERSON_DEFS[key];
                        return (
                          <Bar
                            key={key}
                            dataKey={def.label}
                            stackId="a"
                            fill={def.color}
                            hide={hiddenSeries.has(def.label)}
                          />
                        );
                      })}
                    {visibleExtraSeries.map((s) => (
                      <Bar
                        key={s.key}
                        dataKey={s.label}
                        stackId="a"
                        fill={s.color}
                        hide={hiddenSeries.has(s.label)}
                      />
                    ))}
                    {activeCompareYears.map((y, i) => (
                      <Bar
                        key={`compare-${y}`}
                        dataKey={`compare_${y}`}
                        name={String(y)}
                        fill={COMPARE_YEAR_COLORS[i % COMPARE_YEAR_COLORS.length]}
                        hide={hiddenSeries.has(`compare_${y}`)}
                      />
                    ))}
                    {trendSeries && (
                      <Line
                        dataKey="trend"
                        name="Trend (rest van het jaar)"
                        stroke={TREND_COLOR}
                        strokeWidth={2}
                        strokeDasharray="6 4"
                        dot={{ r: 3, fill: TREND_COLOR }}
                        connectNulls={false}
                        hide={hiddenSeries.has("trend")}
                      />
                    )}
                    <ReferenceLine
                      y={chartAverage}
                      stroke="#dc2626"
                      strokeWidth={1}
                      strokeDasharray="4 3"
                      label={<AverageReferenceLabel value={chartAverage} />}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Tab-strip: Totaal eerst (default), daarna artsen in geconfigureerde volgorde */}
              <div className="flex gap-1">
                {tabDefs.map((tab) => {
                  const active = selectedTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setSelectedTab(tab.key)}
                      className={`px-5 py-3 rounded-t-lg border border-b-0 text-lg font-bold transition ${
                        active
                          ? "bg-white border-gray-200 text-gray-900"
                          : "bg-gray-100 border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                      }`}
                      style={{ boxShadow: `inset 0 ${active ? 3 : 2}px 0 0 ${tab.color}` }}
                    >
                      <span>{tab.label}</span>
                      <span
                        className={`ml-2 tabular-nums text-base font-semibold ${tab.colorClass}`}
                        style={active ? undefined : { opacity: 0.75 }}
                      >
                        {formatCurrency(sumField(periods, tab.totalKey))}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="-mt-px">
                <PeriodBreakdownTable
                  periods={periods}
                  rows={activeTab.rows}
                  compareYears={activeCompareYears}
                  comparePeriodsByYear={comparePeriodsByYear}
                  showTrend={showTrend}
                  trendRowPeriods={trendRowPeriods}
                  note={
                    activeTab.key === TOTAL_TAB_KEY
                      ? "Totaal = praktijkomzet: voor Evelyne en Saskia telt hun huur/commissie mee, niet hun volledige bruto (zij innen zelf en storten een percentage door)."
                      : undefined
                  }
                />
              </div>

              <p className="text-xs text-gray-400 mt-4">
                {periods.length} {GRANULARITEITEN.find((g) => g.key === granulariteit)?.label.toLowerCase()}(en)
                {granulariteit === "week" ? ` — Q${data.kwartaal} ${data.jaar}` : ` — ${data.jaar}`}
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}
